// React
import React, { useEffect, useState, Fragment } from 'react'

// Components
import AddressFields from 'back_office/modules/addresses/components/AddressFields'
import { UserAddressesSelect } from './UserAddressesSelect'
import Span from 'ui/elements/Span'
import A from 'ui/elements/A'
import Div from 'ui/elements/Div'
import Label from 'ui/elements/Label'

// Libraries
import axios from 'axios'

// Hooks
import { useTranslation } from 'shared/utils/useTranslation'
import { useOrm } from 'shared/utils/useOrm'
import { useFormikContext } from 'formik'
import { useSelector } from 'react-redux'
import { useSettings } from 'shared/utils/useSettings'

// Shared
import { addressFieldsOrder } from 'back_office/modules/addresses/components/addressFieldsOrder'
import { fetchCountriesAndProvinces } from 'back_office/modules/addresses/components/fetchCountriesAndProvinces'
import { featureDisabled, FeatureFlags } from 'shared/utils/features'

export const DeliveryFulfillmentForm = () => {
  const [userAddresses, setUserAddresses] = useState([])
  const [isUserAuthenticated, setIsUserAuthenticated] = useState(undefined)

  const form = useFormikContext()
  const { t, tOrEmptyString, Trans } = useTranslation('user')

  const cartId = useSelector((state) => state.client.cartId)

  const cart = useOrm('carts', (carts) => carts.getById(cartId || window.booqableOptions.cart?.id))
  const countries = useOrm('countries', (countries) => {
    return countries.getAll().toArray()
  })
  const provinces = useOrm('provinces', (provinces) => {
    return provinces.getAll().toArray()
  })

  const isAuthEnabled = useSettings('user.auth_enabled')

  const authUriBase = cart?.user_login_url ?? '/customers/login'
  const authRedirectUriParamValue = `${window.location.origin}?${encodeURIComponent(
    'open-picker=true&fulfillment-tab=delivery'
  )}`
  const authUri = `${authUriBase}?redirect_uri=${authRedirectUriParamValue}`

  useEffect(() => {
    if (!cart) return
    if (!isAuthEnabled) return

    const userAddressesUrl = window.booqableOptions?.apiURL ?
      `${window.booqableOptions?.apiURL}/user_addresses` :
      '/user_addresses'

    axios
      .get(userAddressesUrl)
      .then(({ data }) => {
        setIsUserAuthenticated(true)
        setUserAddresses(data.properties)
      })
      .catch(() => {
        setIsUserAuthenticated(false)
      })
  }, [cart?.id, isAuthEnabled])

  useEffect(() => {
    if (!countries.length || !provinces.length) {
      fetchCountriesAndProvinces()
    }
  }, [])

  const handleRedirectToAuth = (e) => {
    if (window.booqableOptions?.isCustomDomainVerified === false) {
      e.preventDefault()

      alert('We\'ve noticed that you set a custom domain but it is not verified yet. Please check your custom domain settings in the back office.')
      return
    }

    window.location.href = authUri
  }

  return (
    <Fragment>
      {isUserAuthenticated && userAddresses.length > 0 && (
        <Div modifiers="margin-bottom-md">
          <Label modifiers="margin-bottom-xs">{t('user.deliveries.choose_saved_address')}</Label>
          <UserAddressesSelect addresses={userAddresses} />
        </Div>
      )}
      <AddressFields
        defaultFormLayout={addressFieldsOrder()}
        skipValidation={featureDisabled(FeatureFlags.NEW_ADDRESS_VALIDATION)}
        invalidExistingAddressWarning={t('user.deliveries.invalid_existing_address_warning')}
        fieldTranslationPrefix="user.deliveries"
        errorKey="user.public_errors.invalid"
        selectPlaceholder={tOrEmptyString('user.checkout.select_placeholder')}
        initialValues={form.initialValues}
        countries={countries}
        provinces={provinces}
        addressIdentifier="delivery_address"
        fieldDebounce={750}
      />
      {isUserAuthenticated === false && isAuthEnabled && (
        <Span modifiers="display-inline-block font-size-md margin-top-md">
          <Trans
            i18nKey="user.deliveries.login"
            components={{
              Link: <A modifiers="font-weight-bold font-size-md text-blue text-decoration-none" href={authUri} onClick={handleRedirectToAuth} />
            }}
          />
        </Span>
      )}
    </Fragment>
  )
}
